import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { CSSObject } from '@emotion/styled';
import { getBackgroundTheme, getBorders } from '../style';
import { generalIcons } from './icons/materialIcons';
import { auth10tixVerification } from '../config/Master';
import { ternaryFunc } from '../utils/helpers';
import Camera from './icons/Camera';
import { ICardProps } from './types/OfferCard';
import ProfileCardBanner from './profile/ProfileCardBanner';
import breakpoints from '../theme/material/breakpoints';
import VerifiedBadgeTooltip from '../components/common/VerifiedBadgeTooltip';
import { CustomStyledProps } from 'style/style';
import { FONT_FAMILIES } from 'theme/material/constants';
import { white } from 'theme/colors';

const CardContainer = styled('div')<{ cardWidth: string | number; cardMargin: string | number; isMatched: boolean }>(
  {
    position: 'relative',
    borderRadius: '6px',
    overflow: 'hidden',
    border: getBorders().PRIMARY,
  },
  ({ cardWidth, cardMargin }) => ({
    width: cardWidth,
    margin: cardMargin,
  }),
  ({ isMatched }) =>
    isMatched && {
      border: '2px solid #E21D4A',
      boxShadow: 'inset 0px 0px 0px 1px rgb(188 32 40)',
    }
);

const PhotoAspectRatio = styled('div')({
  paddingTop: '133%',
  position: 'relative',
});

const AccountDetailsContainer = styled('div')({
  color: 'white',
  position: 'absolute',
  left: '0px',
  bottom: '0px',
  width: '100%',
  zIndex: 3,
});

const AccountDetails = styled('div')(
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'bottom',
    padding: '0 0 10px 10px',
    flexWrap: 'wrap',
    width: '75%',
    textOverflow: 'ellipsis',
  },
  ({ detailsPadding }: CustomStyledProps) => ({
    padding: detailsPadding,
  })
);

const UsernameContainer = styled('div')(() => ({
  width: '100%',
  color: white,
  display: 'flex',
  alignItems: 'center',
}));

const Username = styled('div')<{ overrideWidth: string | number }>(
  {
    width: 'auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    maxWidth: '85%',
    fontSize: 16,
    fontFamily: FONT_FAMILIES.secondary,
    letterSpacing: '-0.5px',
    [breakpoints.down('sm')]: {
      maxWidth: '70%',
    },
  },
  ({ overrideWidth }) => ({
    width: overrideWidth,
  })
);

const UserDetails = styled('div')(() => ({
  fontSize: 10,
  color: getBackgroundTheme().cardDetails,
  width: '85%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const PhotoCountDetails = styled('div')({
  fontSize: 11,
  position: 'absolute',
  padding: '0 10px 10px 0',
  bottom: '0px',
  right: '0px',
  width: '32px',
});

const PhotoCount = styled('span')(
  (): CSSObject => ({
    color: getBackgroundTheme().cardDetails,
    fontSize: 11,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
  })
);

const PublicPhotoCount = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid red',
});

const PrivatePhotoCount = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Gradient = styled('div')({
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%);',
  position: 'absolute',
  width: '100%',
  height: '70px',
  bottom: 0,
  left: 0,
  zIndex: 2,
  [breakpoints.down(639.05)]: {
    height: '58px',
  },
});

const Img = styled('div')<{ imgUrl: string }>(
  {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: '0px',
    left: '0px',
    zIndex: 1,
    borderRadius: '6px',
  },
  ({ imgUrl }) => ({
    backgroundImage: `url(${imgUrl})`,
    backgroundPosition: 'top',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  })
);

const OnlineDot = styled('div')({
  height: '8px',
  width: '8px',
  backgroundColor: 'lightgreen',
  borderRadius: '50%',
  display: 'inline-block',
  marginRight: 3,
});

const StyledPhotoCount = styled('span')({
  alignSelf: 'flex-end',
});

const Icon = styled('span')();

const VerifiedBadge = styled('span')({
  marginLeft: 3,
  zIndex: 2,
  fontSize: 0,
});

const OfferCard = ({
  imgUrl,
  cardWidth,
  cardMargin,
  online,
  username,
  age,
  city,
  photoCount,
  privatePhotoCount,
  hashId,
  onClick,
  id,
  isVerified = false,
  favoritedMe,
  isMatched,
  isSelfieVerified = false,
}: ICardProps): JSX.Element => {
  const memoizedProfileDetails = useMemo(
    () => (
      <Link to={`/profile/${hashId}`}>
        <PhotoAspectRatio>
          <AccountDetailsContainer>
            <AccountDetails style={{ width: '75%' }} className="account-details-container">
              <UsernameContainer>
                <Username
                  data-test-id="data-member-username"
                  overrideWidth={isSelfieVerified && !isVerified ? 'auto' : ternaryFunc(isVerified, 'auto', '100%')}
                >
                  {username}
                </Username>
                {((auth10tixVerification && isVerified) || isSelfieVerified) && (
                  <VerifiedBadge data-test-id="data-verified-badge">
                    <VerifiedBadgeTooltip
                      showToolTip={true}
                      isIdVerified={isVerified}
                      isSelfieVerified={isSelfieVerified}
                      customVerifiedBadgeStyle={{ top: 0, [breakpoints.down(639.05)]: { top: 0 } }}
                    />
                  </VerifiedBadge>
                )}
              </UsernameContainer>
              <UserDetails data-test-id="data-member-age-city">
                {online ? <OnlineDot /> : ''} {age} &middot; {city}
              </UserDetails>
            </AccountDetails>
            <PhotoCountDetails data-test-id="data-member-photo-count">
              <PhotoCount>
                <PublicPhotoCount>
                  <Icon data-test-id="data-camera-icon">
                    <Camera />
                  </Icon>
                  <StyledPhotoCount>{photoCount}</StyledPhotoCount>
                </PublicPhotoCount>
                <PrivatePhotoCount>
                  <Icon data-test-id="data-eye-hidden-icon">
                    <generalIcons.eye.hidden />
                  </Icon>
                  <StyledPhotoCount>{privatePhotoCount}</StyledPhotoCount>
                </PrivatePhotoCount>
              </PhotoCount>
            </PhotoCountDetails>
          </AccountDetailsContainer>
          <Gradient />
          <Img className="data-avatar-image-url" imgUrl={imgUrl} />
        </PhotoAspectRatio>
      </Link>
    ),
    [hashId, imgUrl, username, age, city, photoCount, privatePhotoCount, online, isVerified]
  );

  return (
    <CardContainer
      id={id}
      className="card-items"
      cardWidth={cardWidth}
      cardMargin={cardMargin}
      isMatched={isMatched}
      onClick={onClick}
    >
      <ProfileCardBanner isMatched={isMatched} isFavoritedMe={favoritedMe} />
      {memoizedProfileDetails}
    </CardContainer>
  );
};

export default OfferCard;
